// ============================================================================
// DISBURSEMENT MASTER SCREEN
// ============================================================================
// Master CRUD screen for Disbursement Vouchers via prima-api.
//
// Follows the EngagementMasterScreen pattern with:
//  - FK lookups: employees, clients, engagements, activities (chargeTypeKey='D')
//  - Server-side pagination, search, filter
//  - Column visibility with ColumnSelector
//  - Minimize-to-bar support
//  - Detail rows fetched via getById before opening edit modal

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutScopedEmployeeSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  useDisbursements,
  useCreateDisbursement,
  useUpdateDisbursement,
  useDeleteDisbursement,
} from 'hooks/api/useDisbursement';
import { useEmployees } from 'hooks/api/useEmployee';
import { useEngagements } from 'hooks/api/useEngagement';
import { useActivities } from 'hooks/api/useActivity';
import { disbursementService } from 'api/services/disbursementService';
import type {
  Disbursement,
  DisbursementSearchRequest,
  CreateDisbursementRequest,
  UpdateDisbursementRequest,
} from 'api/types';
import DisbursementFormModal, {
  type DisbursementFormData,
} from 'components/modals/modules/disbursement/DisbursementFormModal';

type ViewMode = 'list' | 'grid';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const formatCurrency = (amount?: number | null): string => {
  if (amount == null) return '-';
  return amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Column definitions for ColumnSelector
const columnDefinitions: ColumnDefinition[] = [
  { id: 'voucherNumber', label: 'Voucher No.',   field: 'voucherNumber', required: true,  defaultVisible: true,  sortable: true },
  { id: 'voucherDate',   label: 'Date',          field: 'voucherDate',   required: false, defaultVisible: true,  sortable: true },
  { id: 'employeeName',  label: 'Employee',       field: 'employeeName',  required: true,  defaultVisible: true,  sortable: true },
  { id: 'totalAmount',   label: 'Total Amount',   field: 'totalAmount',   required: false, defaultVisible: true,  sortable: true },
  { id: 'status',        label: 'Status',          field: 'statusName',    required: false, defaultVisible: true,  sortable: true },
  { id: 'remarks',       label: 'Remarks',         field: 'remarks',       required: false, defaultVisible: false, sortable: false },
  { id: 'createdAt',     label: 'Created',         field: 'createdAt',     required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',     label: 'Updated',         field: 'updatedAt',     required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'disbursement-master-columns';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DisbursementMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DisbursementMasterScreen: React.FC<DisbursementMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  // Visibility scope is enforced server-side on the disbursement-entry module.
  // Anchor cascades through tbl_disbursement_detail.engagement_id:
  //   view-all-employees → unrestricted
  //   view-own-team      → list shows headers with at least one detail whose
  //                        engagement is reachable (manager_id ∈ team OR
  //                        client.partner_id ∈ team)
  //   neither            → headers reachable via the caller's own (manager_id /
  //                        partner_id) engagements only
  // Header.employee_id (payee) is NOT scope-anchored — the Add New employee
  // dropdown is intentionally loose; backend rejects out-of-scope detail rows
  // (i.e. an engagement the caller can't see) with 403.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _permissions = {
    canViewAll: allowedActions.includes('view-all-employees'),
    canViewTeam: allowedActions.includes('view-own-team'),
  };

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --------------------------------------------------------------------------
  // Column visibility state
  // --------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // --------------------------------------------------------------------------
  // Lookup data
  // --------------------------------------------------------------------------
  const { data: employeesRaw, refetch: refetchEmployees } = useEmployees({
    page: 1,
    pageSize: 200,
    includeInactive: false,
  });
  // Engagements pre-loaded only as seed for label resolution in edit mode.
  // The form's per-row Engagement field is async-search (SyutEngagementSelect),
  // so a small page size is fine — typed search hits the server directly.
  const { data: engagementsRaw, refetch: refetchEngagements } = useEngagements({
    page: 1,
    pageSize: 500,
    includeInactive: false,
  });
  // Activities — fetched server-side filtered to chargeTypeKey='D' (disbursement
  // activities only). 500 is generous; if a firm ever exceeds this we should
  // switch to an async-search SyutSelect instead of a preload.
  const { data: activitiesRaw, refetch: refetchActivities } = useActivities({
    page: 1,
    pageSize: 500,
    chargeTypeKey: 'D',
    includeInactive: false,
  });

  const employeeOptions = useMemo(
    () => employeesRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` })),
    [employeesRaw],
  );

  // Engagement seed options — label format mirrors SyutEngagementSelect
  // ("Client - Engagement Type - Year (CODE)") so already-saved selections
  // resolve to a stable label in edit mode.
  const engagementOptions = useMemo(
    () =>
      engagementsRaw.map((eng) => {
        const parts = [eng.clientName, (eng as any).engagementTypeName, eng.year].filter(Boolean);
        const base = parts.join(' - ');
        return {
          value: eng.id,
          label: eng.code ? `${base} (${eng.code})` : base,
        };
      }),
    [engagementsRaw],
  );

  // Activity options — list comes pre-filtered from the server (chargeTypeKey='D').
  const activityOptions = useMemo(
    () => activitiesRaw.map((a) => ({ value: a.id, label: `${a.code} - ${a.name}` })),
    [activitiesRaw],
  );

  // Filter options for the filter panel
  const employeeFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...employeesRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` }))],
    [employeesRaw],
  );

  const loadLookups = useCallback(() => {
    refetchEmployees();
    refetchEngagements();
    refetchActivities();
  }, [refetchEmployees, refetchEngagements, refetchActivities]);

  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<DisbursementSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, loading, refetch } = useDisbursements(queryParams);

  // --------------------------------------------------------------------------
  // View state
  // --------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [voucherNumberFilter, setVoucherNumberFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Modal state
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Disbursement | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.voucherNumber === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data, loadLookups],
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Disbursement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createDisbursement } = useCreateDisbursement();
  const { updateDisbursement } = useUpdateDisbursement();
  const { deleteDisbursement } = useDeleteDisbursement();

  // --------------------------------------------------------------------------
  // Column definitions for SyutGrid
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Disbursement>[] = useMemo(
    () => [
      {
        id: 'voucherNumber',
        header: 'Voucher No.',
        accessorKey: 'voucherNumber',
        size: 150,
        enableSorting: true,
      },
      {
        id: 'voucherDate',
        header: 'Date',
        accessorKey: 'voucherDate',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.voucherDate),
      },
      {
        id: 'employeeName',
        header: 'Employee',
        accessorKey: 'employeeName',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => row.original.employeeName || '-',
      },
      {
        id: 'totalAmount',
        header: 'Total Amount',
        accessorKey: 'totalAmount',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatCurrency(row.original.totalAmount)}</div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 100,
        enableSorting: true,
        cell: ({ row }) => {
          const code = row.original.statusCode;
          let badgeClass = 'inactive';
          if (code === 'APPROVED') badgeClass = 'active';
          else if (code === 'SUBMITTED') badgeClass = 'info';
          return (
            <span className={`badge badge-${badgeClass}`}>
              {row.original.statusName || code}
            </span>
          );
        },
      },
      {
        id: 'remarks',
        header: 'Remarks',
        accessorKey: 'remarks',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => (
          <div
            title={row.original.remarks || ''}
            style={{
              maxWidth: '180px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.remarks || '-'}
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    [],
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Disbursement>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns],
  );

  // --------------------------------------------------------------------------
  // Handlers -- open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (record: Disbursement) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.voucherNumber}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      // Fetch full record with details before opening modal
      try {
        const fullRecord = await disbursementService.getById(record.id);
        setSelectedRecord(fullRecord.data);
        setFormMode('edit');
        setFormApiError(null);
        setFormModalOpen(true);
      } catch {
        notificationService.toast.error('Failed to load disbursement details.');
      }
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleDeleteClick = useCallback((record: Disbursement) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Disbursement | null>(null);

  const handleView = useCallback(async (record: Disbursement) => {
    try {
      const fullRecord = await disbursementService.getById(record.id);
      setViewRecord(fullRecord.data);
      setViewModalOpen(true);
    } catch {
      notificationService.toast.error('Failed to load disbursement details.');
    }
  }, []);

  // --------------------------------------------------------------------------
  // Save handler
  // --------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: DisbursementFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        if (formMode === 'create') {
          const payload: CreateDisbursementRequest = {
            employeeId: formData.employeeId,
            voucherDate: formData.voucherDate,
            remarks: formData.remarks || undefined,
            statusCode: formData.statusCode,
            details: formData.details.map((d, idx) => ({
              lineNumber: idx + 1,
              voucherDate: d.voucherDate,
              engagementId: d.engagementId,
              activityId: d.activityId,
              amount: parseFloat(d.amount) || 0,
              remarks: d.remarks || undefined,
            })),
          };
          const created = await createDisbursement(payload);
          notificationService.toast.success(
            `Disbursement "${created?.voucherNumber || ''}" created.`,
          );
        } else if (selectedRecord) {
          const payload: UpdateDisbursementRequest = {
            employeeId: formData.employeeId,
            voucherDate: formData.voucherDate,
            remarks: formData.remarks || undefined,
            statusCode: formData.statusCode,
            details: formData.details.map((d, idx) => ({
              lineNumber: idx + 1,
              voucherDate: d.voucherDate,
              engagementId: d.engagementId,
              activityId: d.activityId,
              amount: parseFloat(d.amount) || 0,
              remarks: d.remarks || undefined,
            })),
            rowVersion: selectedRecord.rowVersion,
          };
          await updateDisbursement(selectedRecord.id, payload);
          notificationService.toast.success(
            `Disbursement "${selectedRecord.voucherNumber}" updated.`,
          );
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
        loadLookups();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save disbursement. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createDisbursement, updateDisbursement, refetch, loadLookups],
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteDisbursement(deleteTarget.id);
      notificationService.toast.success(`Disbursement "${deleteTarget.voucherNumber}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete disbursement.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteDisbursement, refetch]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.voucherNumber) count++;
    if (queryParams.employeeId) count++;
    if (queryParams.fromDate) count++;
    if (queryParams.toDate) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.voucherNumber, queryParams.employeeId, queryParams.fromDate, queryParams.toDate, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      voucherNumber: voucherNumberFilter || undefined,
      employeeId: employeeFilter || undefined,
      fromDate: dateFromFilter || undefined,
      toDate: dateToFilter || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [voucherNumberFilter, employeeFilter, dateFromFilter, dateToFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setVoucherNumberFilter('');
    setEmployeeFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Disbursement): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [
        {
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        },
      ];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick],
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'New Disbursement Voucher',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch],
  );

  // Form initial data (edit mode) — maps full Disbursement record to DisbursementFormData
  const formInitialData: DisbursementFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;

    let rowCounter = 0;
    return {
      voucherNumber: selectedRecord.voucherNumber,
      employeeId: selectedRecord.employeeId || '',
      voucherDate: selectedRecord.voucherDate ? selectedRecord.voucherDate.substring(0, 10) : '',
      remarks: selectedRecord.remarks || '',
      statusCode: (selectedRecord.statusCode as 'DRAFT' | 'SUBMITTED' | 'APPROVED') || 'DRAFT',
      details: (selectedRecord.details || []).map((d) => ({
        id: `row-${++rowCounter}`,
        voucherDate: d.voucherDate ? d.voucherDate.substring(0, 10) : '',
        engagementId: d.engagementId || '',
        activityId: d.activityId || '',
        // Carry the joined label fields so the form can seed the per-row
        // Activity dropdown when the saved activity isn't in the active
        // chargeType='D' preload (deactivated / reclassified / >500 rows).
        activityCode: d.activityCode || undefined,
        activityName: d.activityName || undefined,
        amount: String(d.amount ?? ''),
        remarks: d.remarks || '',
      })),
    };
  }, [formMode, selectedRecord]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Disbursement Entry"
        description="Record expenses incurred by employees for client engagements"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.dollarSign size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search voucher, employee...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* List view */}
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={loading ? 'Loading disbursements...' : 'No disbursements found'}
              />
            )}

            {/* Grid (card) view */}
            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {loading ? 'Loading disbursements...' : 'No disbursements found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const code = item.statusCode;
                    let badgeClass = 'inactive';
                    if (code === 'APPROVED') badgeClass = 'active';
                    else if (code === 'SUBMITTED') badgeClass = 'info';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {item.voucherNumber}
                            </div>
                            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                              {item.employeeName || '-'}
                            </div>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                              {formatDate(item.voucherDate)}
                            </div>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            {formatCurrency(item.totalAmount)}
                          </span>
                          <span className={`badge badge-${badgeClass}`}>
                            {item.statusName || code}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button
                className="search-panel-close"
                onClick={() => setFilterOpen(false)}
                title="Close filters"
              >
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Voucher Number
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={voucherNumberFilter}
                  onChange={(e) => setVoucherNumberFilter(e.target.value)}
                  placeholder="Filter by voucher..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <SyutScopedEmployeeSelect
                  label="Employee"
                  scopeMode="view"
                  moduleId="disbursement-entry"
                  value={employeeFilter || undefined}
                  onChange={(val) => setEmployeeFilter(val || '')}
                  isClearable
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Date From
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Date To
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form modal */}
      <DisbursementFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        employeeOptions={employeeOptions}
        engagementOptions={engagementOptions}
        activityOptions={activityOptions}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Disbursement"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.voucherNumber}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {/* View Modal — read-only disbursement details */}
      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-lg">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Disbursement - {viewRecord.voucherNumber}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setViewModalOpen(false);
                  setViewRecord(null);
                }}
                title="Close"
              >
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2" style={{ marginBottom: '1rem' }}>
                <div>
                  <label className="form-label">Voucher Number</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.voucherNumber || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Employee</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.employeeName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Voucher Date</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.voucherDate)}</div>
                </div>
                <div>
                  <label className="form-label">Total Amount</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatCurrency(viewRecord.totalAmount)}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'APPROVED' ? 'active' : viewRecord.statusCode === 'SUBMITTED' ? 'info' : 'inactive'}`}>
                      {viewRecord.statusName || viewRecord.statusCode}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="form-label">Remarks</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.remarks || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Created</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.createdAt)}{viewRecord.createdByName ? ` by ${viewRecord.createdByName}` : ''}</div>
                </div>
                {viewRecord.updatedAt && (
                  <div>
                    <label className="form-label">Updated</label>
                    <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDate(viewRecord.updatedAt)}{viewRecord.updatedByName ? ` by ${viewRecord.updatedByName}` : ''}</div>
                  </div>
                )}
              </div>

              {/* Detail lines */}
              {viewRecord.details && viewRecord.details.length > 0 && (
                <>
                  <h4
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      margin: '0.75rem 0 0.5rem',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Detail Lines
                  </h4>
                  <div
                    style={{
                      border: '1px solid var(--form-border-color)',
                      borderRadius: '4px',
                      overflow: 'auto',
                    }}
                  >
                    <table
                      style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}
                    >
                      <thead>
                        <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>
                            #
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>
                            Date
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>
                            Engagement
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>
                            Activity
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                            Amount
                          </th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>
                            Remarks
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewRecord.details.map((d, idx) => (
                          <tr
                            key={d.id || idx}
                            style={{ borderBottom: '1px solid var(--border-color)' }}
                          >
                            <td style={{ padding: '0.375rem 0.5rem' }}>{d.lineNumber}</td>
                            <td style={{ padding: '0.375rem 0.5rem' }}>
                              {formatDate(d.voucherDate)}
                            </td>
                            <td style={{ padding: '0.375rem 0.5rem' }}>
                              {d.engagementCode || d.engagementId}
                            </td>
                            <td style={{ padding: '0.375rem 0.5rem' }}>
                              {d.activityName || d.activityCode || d.activityId}
                            </td>
                            <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>
                              {formatCurrency(d.amount)}
                            </td>
                            <td style={{ padding: '0.375rem 0.5rem' }}>{d.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setViewModalOpen(false);
                  setViewRecord(null);
                }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setViewModalOpen(false);
                  setViewRecord(null);
                  handleEdit(viewRecord);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DisbursementMasterScreen;
